<template>
    <div
        :class="['page', 'page--light', {'page--partner-app page--gradient-background':isPartnerApp}]"
    >
        <section :class="['row', {'header':!isPartnerApp}]">
            <div :class="['wrapper wrapper--no-max-width',{'wrapper--partner-app':isPartnerApp}]">
                <header class="row__header">
                    <back-button
                        :isAbsolute="false"
                        :isBlack="true"
                        :isHiddenOnDesktop="true"
                    />
                    <h1 id="fanshop-lobby-title">
                        {{ $t('rewards.fanShop.title') }}
                    </h1>
                    <div v-if="isPartnerApp"
                        class="subcategories-filter"
                    >
                        <subcategory-filter-toogle />
                    </div>
                    <partner-filter-toggle v-else />
                </header>
            </div>
        </section>
        <div class="categories">
            <div v-if="isPartnerApp">
                <div class="wrapper">
                    <products-list
                        :variants="partnerProducts"
                        :subCategories="partnerProductsSubCategories"
                        :loadingProducts="loadingPartnerProducts"
                        :loadingCategory="loadingPartnerCategory"
                        :selectedMaxChips="3"
                        @update:productsByFilter="handleUpdateProductsByFilter"
                        @update:productsByPage="handleUpdateProductsByPage"
                    />
                </div>
            </div>
            <div v-else>
                <h2 v-if="!loading && !getProductParentCategories.length"
                    class="categories__not-found"
                >
                    {{ $t('rewards.fanShop.noCategories') }}
                </h2>

                <div v-for="category in getProductParentCategories"
                    :key="category.id"
                    class="categories__category"
                >
                    <section class="row">
                        <div class="wrapper wrapper--no-max-width">
                            <div v-if="category.subCategories"
                                class="row__header"
                            >
                                <h1 class="categories__title">{{ getTranslatedStr(category, 'name', category.name) }}</h1>
                                <router-link class="row__header-link"
                                    :to="`/rewards/fan-rewards/categories/${category.id}`"
                                >
                                    {{ $t('global.seeAll') }}
                                </router-link>
                            </div>
                            <carousel v-if="category.subCategories"
                                :navigationEnabled="true"
                                navigation-next-label=""
                                navigation-prev-label=""
                                :paginationEnabled="false"
                                :scrollPerPage="true"
                                :perPageCustom="perPageCarouselBreakpoints"
                                class="carousel"
                            >
                                <slide v-for="subcat in category.subCategories"
                                    :key="subcat.id"
                                >
                                    <tile :id="`tile-${subcat.id}`"
                                        :imageUrl="getImage(subcat)"
                                        :linkUrl="getLink(category.id, subcat.id)"
                                        :name="getTranslatedStr(subcat, 'name', subcat.name)"
                                    />
                                </slide>
                            </carousel>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Socios variable
import socios from '@webapp/socios.json';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import tile from '@webapp/modules/rewards/components/fanshop/tile.vue';
import partnerFilterToggle from '@webapp/modules/rewards/components/fanshop/partner-filter-toggle.vue';
import productsList from '@webapp/modules/rewards/components/fanshop/products-list.vue';
import subcategoryFilterToogle from '@webapp/modules/rewards/components/fanshop/partner/subcategory-filter-toggle.vue';

import {
    Carousel,
    Slide,
} from 'vue-carousel';

// Local Types
import localStoreTypes from '@webapp/store/types.js';

// Types
import {
    RewardsRemoteConfigKeys,
} from '@shared/modules/rewards/types/constants';
import {
    EventNames,
} from '@shared/utils/analytics-keys';

// Store
import {
    TUTORIAL_NAMES,
} from '@webapp/store/modules/tutorial';
import sharedEpicTypes from '@shared/epics/types';

export default {
    name: 'fanshop-lobby',
    components: {
        backButton,
        tile,
        Carousel,
        Slide,
        partnerFilterToggle,
        productsList,
        subcategoryFilterToogle,
    },
    data() {
        return {
            loading: false,
            loadingPartnerProducts: true,
            loadingPartnerCategory: true,
            pageLimit: 15,
            selectedSubcategories: [],
        };
    },
    computed: {
        ...mapGetters(['getTranslatedStr', 'isPartnerApp', 'partnerAppData']),

        ...mapGetters('fanshop', [
            'getProductParentCategories',
            'getProductSubCategoriesById',
            'getProductVariantsByCategory',
            'getSubCategoriesFilterById',
        ]),

        ...mapGetters('tutorial', ['tutorialStatus']),

        ...mapGetters('remoteConfig', ['remoteConfigByKey']),

        ...mapGetters('partners', [
            'getPartnersFiltered',
            'getAllPartnersFiltered',
        ]),

        perPageCarouselBreakpoints() {
            return [
                [320, 1],
                [450, 1],
                [500, 1.2],
                [600, 1.3],
                [700, 1.5],
                [768, 0.95],
                [900, 1.2],
                [1000, 1.4],
                [1100, 1.6],
                [1200, 1.8],
                [1300, 1.8],
                [1400, 2.1],
                [1500, 2.3],
                [1600, 2.6],
                [1800, 3],
                [2000, 3.4],
            ];
        },
        partnerProducts() {
            return this.getProductVariantsByCategory.data || [];
        },
        routeToHome() {
            return {
                path: '/home/overview',
            };
        },
        partnerProductsSubCategories() {
            return Object.values(this.getProductSubCategoriesById);
        },
        partnerProductsSubCategoriesIds() {
            return Object.keys(this.getProductSubCategoriesById);
        },
        partnerIds() {
            return this.isPartnerApp ? [this.partnerAppData?.id] : (this.getPartnersFiltered()?.map((partner) => partner.id) || []);
        },
        subCategoryIdsByUrlParams() {
            const params = new URLSearchParams(this.$route.hash.replace('#', '?'));
            const subCatIds = params.get('selected')?.length ? [...params.get('selected').split(',')] : [];

            return subCatIds.length ? subCatIds : [];
        },
    },
    watch: {
        async 'getAllPartnersFiltered.default'() {
            await this.loadCategories();
        },
        async 'getSubCategoriesFilterById'(filteredSubCategories) {
            if (filteredSubCategories.length === this.partnerProductsSubCategoriesIds.length) {
                await this.loadProductsForCategory([]);

            } else {
                await this.loadProductsForCategory(filteredSubCategories);
            }
        },

        loading(val) {
            if (!this.isPartnerApp) {
                this.showAppLoader({
                    isVisible: val,
                    hasOpacity: true,
                });
            }
        },
    },
    async mounted() {
        if (!this.remoteConfigByKey(RewardsRemoteConfigKeys.IsWebFanRewardEnabled)) {
            this.$router.replace('/404');

            return;
        }

        await this.getWallet({
            chilizCurrencyId: socios.chilizCurrencyId,
        });

        await this.loadCategories();
        if (this.isPartnerApp && this.partnerAppData?.id) {
            this.updateSubCategoriesFilterById(this.subCategoryIdsByUrlParams);
        }
        await this.showFanShopIntro();
        this.$webTrackingInstance?.sendEvent(EventNames.FanRewardsLobby);
    },
    methods: {
        ...mapActions('fanshop', ['fetchProductCategories', 'fetchProductsByCategoryId', 'updateSubCategoriesFilterById']),

        ...mapActions('tutorial', ['toggleTutorial']),

        ...mapActions({
            showAppLoader: localStoreTypes.SET_APP_LOADER,
            setModal: localStoreTypes.SET_MODAL,
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        async loadCategories() {
            if (this.isPartnerApp) {
                this.loadingPartnerCategory = true;
            } else {
                this.loading = true;
            }

            await this.fetchProductCategories({
                partnerIds: this.partnerIds,
            });

            if (this.isPartnerApp) {
                this.loadingPartnerCategory = false;
            } else {
                this.loading = false;
            }
        },
        async loadProductsForCategory(subCategories = [], page = 1, append = false) {
            this.loadingPartnerProducts = true;

            await this.fetchProductsByCategoryId({
                categoryIds: subCategories,
                partnerIds: this.partnerIds,
                limit: this.pageLimit,
                page,
                append,
                getAlsoOutOfStockProducts: true,
            });
            this.loadingPartnerProducts = false;
        },

        getImage(cat) {
            if (cat.images && cat.images.length) {
                return cat.images.find((image) => image.imageType.type === 'banner').url;
            }

            return '';
        },

        getLink(cat, subcat) {
            return `/rewards/fan-rewards/categories/${cat}#selected=${subcat}`;
        },

        showFanShopIntro() {
            if (!this.tutorialStatus[TUTORIAL_NAMES.FAN_SHOP_INTRO_TUTORIAL]) {
                this.setModal({
                    componentToLoad: 'fan-shop-intro-sheet',
                    isVisible: false,
                    isClosable: true,
                    closeCallback: () => {
                        this.toggleTutorial({
                            tutorialName: TUTORIAL_NAMES.FAN_SHOP_INTRO_TUTORIAL,
                            value: true,
                        });
                    },
                });
            }
        },

        handleUpdateProductsByFilter(selectedSubcategoriesIds) {
            this.updateSubCategoriesFilterById(selectedSubcategoriesIds);
        },
        async handleUpdateProductsByPage(selectedSubcategoriesIds, page) {
            await this.loadProductsForCategory(selectedSubcategoriesIds, page);
        },
    },
};
</script>

<style lang="scss" scoped>
.page {
    &--partner-app {
        min-height: 100vh;
    }
}

.carousel {
    /* stylelint-disable-next-line selector-class-pattern */
    ::v-deep .VueCarousel-slide {
        flex-basis: unset;
    }
}

.subcategories-filter {
    display: block;
    @include min-width(md) {
        display: none;
    }
}

.header {
    background-color: $white-color;

    h1 {
        font-size: 3rem;
    }
}

.wrapper {
    @include min-width(md) {
        width: 85%;
    }

    @include min-width(xl) {
        width: 87%;
    }

    &--partner-app {
        width: 98%;
    }
}

.categories {
    margin: 0;

    &__title {
        font-size: 2.5rem;
        margin: 3rem 0;
    }

    &__category {
        background: linear-gradient(180deg, $white-color 0%, $athens-gray-color 100%);
    }

    .row__header-link {
        margin-right: 1rem;
    }

    &__not-found {
        text-align: center;
    }

    &__container {
        display: grid;
        grid-template-columns: repeat(auto-fill, 46%);
        grid-gap: 2rem;
        justify-content: center;

        ::v-deep {
            .tile {
                max-width: 18rem;
                margin: auto;
            }
        }

        @include min-width(sm) {
            grid-gap: 4rem;
            grid-template-columns: repeat(auto-fill, 18rem);
            justify-content: start;
        }
    }

    ::v-deep .chips-carousel {
        margin: 3rem 0 3rem 0;
    }
}
</style>